import { cn } from '@popow/infra/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import * as React from 'react';

// Lottie를 dynamic import로 로드 (SSR 방지)
const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

/**
 * Button Component
 *
 * PDS (Popow Design System) Button component built on shadcn UI base.
 *
 * ## PDS Variants (디자인 시스템)
 * ⚠️ IMPORTANT: Only 18 specific PDS button combinations are allowed.
 * Refer to: `docs/guides/design-system/06-component-styling.md`
 *
 * 📊 Semantic Token Mapping:
 * - Solid Primary: 초록 배경 + 검정 텍스트
 * - Outlined Primary: 회색 테두리 + 초록 텍스트
 * - Solid Alternative: 다크그레이 배경 + 흰색 텍스트
 * - Outlined Alternative: 초록 테두리 + 초록 텍스트
 * - Solid Assistive: 밝은회색 배경 + 회색 텍스트
 * - Outlined Assistive: 회색 테두리 + 회색 텍스트
 * - Ghost Assistive: 밑줄 + 회색 텍스트 (CTA only)
 *
 * @example
 * ```tsx
 * // PDS CTA Buttons (56px)
 * <Button variant="cta-solid-primary">Get Started</Button>
 * <Button variant="cta-solid-alternative">Learn More</Button>
 *
 * // PDS Large Buttons (48px)
 * <Button variant="lg-solid-primary">Submit</Button>
 * <Button variant="lg-outlined-primary">Edit</Button>
 *
 * // PDS Small Buttons (36px)
 * <Button variant="sm-solid-primary">Save</Button>
 *
 * // With Icons
 * <Button variant="lg-solid-primary" leftIcon={<CheckIcon />}>
 *   Confirm
 * </Button>
 *
 * // Loading State (PDS only)
 * <Button variant="cta-solid-primary" isLoading>
 *   Processing...
 * </Button>
 *
 * // Icon Only Button (44x44px)
 * <Button iconOnly aria-label="Favorite">
 *   <HeartIcon />
 * </Button>
 * ```
 */

const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center font-mona-sans transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-semantic-primary-normal focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        // ===== PDS CTA GROUP (56px) =====
        'cta-solid-primary':
          'h-14 w-full gap-2 rounded-xl bg-semantic-primary-normal px-5 font-medium text-[17px] text-semantic-label-strong leading-[1.412] tracking-[-0.01em] active:bg-primary-overlay disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'cta-solid-alternative':
          'h-14 w-full gap-2 rounded-xl bg-atomic-cool-neutral-800 px-5 font-medium text-[17px] text-semantic-inverse-label leading-[1.412] tracking-[-0.01em] active:bg-atomic-cool-neutral-800/[0.88] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'cta-outlined-assistive':
          'h-14 w-full gap-2 rounded-xl border border-atomic-cool-neutral-500/[0.22] bg-transparent px-5 font-medium text-[17px] text-atomic-cool-neutral-800/[0.88] leading-[1.412] tracking-[-0.01em] active:bg-black/[0.03] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        'cta-ghost-assistive':
          'relative h-14 w-full gap-2 rounded-xl bg-transparent px-5 font-medium text-[17px] text-atomic-cool-neutral-800/[0.88] leading-[1.412] tracking-[-0.01em] active:bg-black/[0.03] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28] [&>span]:border-atomic-cool-neutral-500/[0.52] [&>span]:border-b [&>span]:pb-[2px] disabled:[&>span]:border-atomic-cool-neutral-500/[0.16]',

        // ===== PDS CTA GROUP - MEDIUM (44px) =====
        'cta-solid-assistive-medium':
          'w-full gap-1 rounded-lg bg-atomic-cool-neutral-500/[0.08] px-5 py-3 font-normal text-[15px] text-atomic-cool-neutral-800/[0.88] leading-[1.334] tracking-[-0.01em] hover:bg-atomic-cool-neutral-500/[0.10] active:bg-atomic-cool-neutral-500/[0.16] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        // ===== PDS LARGE GROUP (48px) =====
        'lg-solid-primary':
          'h-12 gap-1.5 rounded-xl bg-semantic-primary-normal px-5 font-medium text-[16px] text-semantic-label-strong leading-[1.375] tracking-[-0.01em] active:bg-primary-overlay disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'lg-solid-alternative':
          'h-12 gap-1.5 rounded-xl bg-atomic-cool-neutral-800 px-5 font-medium text-[16px] text-semantic-inverse-label leading-[1.375] tracking-[-0.01em] active:bg-atomic-cool-neutral-800/[0.88] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'lg-solid-assistive':
          'h-12 gap-1.5 rounded-xl bg-atomic-cool-neutral-500/[0.08] px-5 font-medium text-[16px] text-atomic-cool-neutral-800/[0.88] leading-[1.375] tracking-[-0.01em] hover:bg-atomic-cool-neutral-500/[0.10] active:bg-atomic-cool-neutral-500/[0.16] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'lg-outlined-primary':
          'h-12 gap-1.5 rounded-xl border border-atomic-cool-neutral-500/[0.22] bg-transparent px-5 font-medium text-[16px] text-semantic-primary-strong leading-[1.375] tracking-[-0.01em] active:bg-semantic-primary-normal/[0.06] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        'lg-outlined-alternative':
          'h-12 gap-1.5 rounded-xl border border-semantic-primary-normal bg-transparent px-5 font-medium text-[16px] text-semantic-primary-strong leading-[1.375] tracking-[-0.01em] active:bg-semantic-primary-normal/[0.06] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        'lg-outlined-assistive':
          'h-12 gap-1.5 rounded-xl border border-atomic-cool-neutral-500/[0.22] bg-transparent px-5 font-medium text-[16px] text-atomic-cool-neutral-800/[0.88] leading-[1.375] tracking-[-0.01em] active:bg-black/[0.03] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        // ===== PDS SMALL GROUP (36px) =====
        'sm-solid-primary':
          'gap-1 rounded-lg bg-semantic-primary-normal px-4 py-2 font-normal text-[15px] text-semantic-label-strong leading-[1.334] tracking-[-0.01em] active:bg-primary-overlay disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'sm-solid-alternative':
          'gap-1 rounded-lg bg-atomic-cool-neutral-800 px-4 py-2 font-normal text-[15px] text-semantic-inverse-label leading-[1.334] tracking-[-0.01em] active:bg-atomic-cool-neutral-800/[0.88] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'sm-solid-assistive':
          'gap-1 rounded-lg bg-atomic-cool-neutral-500/[0.08] px-4 py-2 font-normal text-[15px] text-atomic-cool-neutral-800/[0.88] leading-[1.334] tracking-[-0.01em] hover:bg-atomic-cool-neutral-500/[0.10] active:bg-atomic-cool-neutral-500/[0.16] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28]',

        'sm-outlined-primary':
          'gap-1 rounded-lg border border-atomic-cool-neutral-500/[0.22] bg-transparent px-4 py-2 font-normal text-[15px] text-semantic-primary-strong leading-[1.334] tracking-[-0.01em] active:bg-semantic-primary-normal/[0.06] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        'sm-outlined-alternative':
          'gap-1 rounded-lg border border-semantic-primary-normal bg-transparent px-4 py-2 font-normal text-[15px] text-semantic-primary-strong leading-[1.334] tracking-[-0.01em] active:bg-semantic-primary-normal/[0.06] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',

        'sm-outlined-assistive':
          'gap-1 rounded-lg border border-atomic-cool-neutral-500/[0.22] bg-transparent px-4 py-2 font-normal text-[15px] text-atomic-cool-neutral-800/[0.88] leading-[1.334] tracking-[-0.01em] active:bg-black/[0.03] disabled:border-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-750/[0.28]',
      },

      /**
       * Size variants (for shadcn UI compatibility)
       * Note: For icon-only buttons, use iconOnly prop instead
       */
      size: {
        default: '',
        sm: '',
        lg: '',
      },

      /**
       * Loading state (PDS only)
       */
      isLoading: {
        true: 'relative',
        false: '',
      },

      /**
       * Icon Only mode
       * 44×44px square button for icons only
       */
      iconOnly: {
        true: 'h-11 w-11 rounded-lg bg-transparent p-0 text-atomic-cool-neutral-800/[0.88] hover:bg-atomic-cool-neutral-500/[0.08] active:bg-atomic-cool-neutral-500/[0.16] disabled:bg-semantic-interaction-disable disabled:text-atomic-cool-neutral-750/[0.28] [&>svg]:h-6 [&>svg]:w-6',
        false: '',
      },
    },

    /**
     * Compound variants: Loading 상태에서도 Pressed와 동일한 스타일 적용
     */
    compoundVariants: [
      // Solid Primary: Loading 시 black 12% overlay + Label 색상 유지
      {
        variant: ['cta-solid-primary', 'lg-solid-primary', 'sm-solid-primary'],
        isLoading: true,
        className: 'bg-primary-overlay disabled:text-semantic-label-strong',
      },
      // Solid Alternative: Loading 시 Cool Neutral/800 88%
      {
        variant: ['cta-solid-alternative', 'lg-solid-alternative', 'sm-solid-alternative'],
        isLoading: true,
        className:
          'bg-atomic-cool-neutral-800/[0.88] disabled:bg-atomic-cool-neutral-800/[0.88] disabled:text-semantic-inverse-label',
      },
      // Solid Assistive: Loading 시 Cool Neutral/500 16%
      {
        variant: ['cta-solid-assistive-medium', 'lg-solid-assistive', 'sm-solid-assistive'],
        isLoading: true,
        className: 'bg-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-800/[0.88]',
      },
      // Outlined Primary/Alternative: Loading 시 Primary 6% overlay
      {
        variant: [
          'lg-outlined-primary',
          'lg-outlined-alternative',
          'sm-outlined-primary',
          'sm-outlined-alternative',
        ],
        isLoading: true,
        className: 'bg-semantic-primary-normal/[0.06] disabled:text-semantic-primary-strong',
      },
      // Outlined Assistive: Loading 시 black 3% overlay
      {
        variant: ['cta-outlined-assistive', 'lg-outlined-assistive', 'sm-outlined-assistive'],
        isLoading: true,
        className: 'bg-black/[0.03] disabled:text-atomic-cool-neutral-800/[0.88]',
      },
      // Ghost Assistive: Loading 시 black 3% overlay + 밑줄 제거
      {
        variant: 'cta-ghost-assistive',
        isLoading: true,
        className:
          '[&>span]:!border-none bg-black/[0.03] disabled:text-atomic-cool-neutral-800/[0.88]',
      },
      // IconOnly: Loading 시 회색 88% 유지
      {
        iconOnly: true,
        isLoading: true,
        className: 'bg-atomic-cool-neutral-500/[0.16] disabled:text-atomic-cool-neutral-800/[0.88]',
      },
    ],

    defaultVariants: {
      variant: 'lg-solid-primary',
      size: 'default',
      isLoading: false,
      iconOnly: false,
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;

  /**
   * Left icon element (PDS only)
   */
  leftIcon?: React.ReactNode;

  /**
   * Right icon element (PDS only)
   */
  rightIcon?: React.ReactNode;

  /**
   * Loading state - shows spinner (PDS only)
   */
  isLoading?: boolean;

  /**
   * Loading spinner component (optional custom spinner) (PDS only)
   */
  loadingSpinner?: React.ReactNode;

  /**
   * CTA only: make button full width (PDS only)
   */
  fullWidth?: boolean;
}

// Helper to determine if variant is CTA (including medium)
const isCTAVariant = (variant: ButtonProps['variant']) => {
  return variant?.startsWith('cta-') || false;
};

// Helper to determine if variant is Ghost (needs custom underline)
const isGhostVariant = (variant: ButtonProps['variant']) => {
  return variant === 'cta-ghost-assistive';
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isLoading,
      iconOnly,
      leftIcon,
      rightIcon,
      loadingSpinner,
      asChild = false,
      children,
      disabled,
      fullWidth,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Determine if this is a CTA button (loading text should stay visible)
    const isCTA = isCTAVariant(variant);

    // Determine if this is a Ghost button (needs custom underline)
    const isGhost = isGhostVariant(variant);

    // Non-interactive state (disabled or loading)
    const nonInteractive = Boolean(disabled || isLoading);

    // Click handler with keyboard/programmatic prevention
    const handleClick: React.MouseEventHandler<HTMLElement> = e => {
      if (nonInteractive) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    };

    // Lottie JSON 파일 로드 (버튼 타입에 따라 black/white 선택)
    // Lottie 애니메이션 데이터 타입 (lottie-react와 호환)
    type LottieAnimationData = Record<string, unknown>;
    const [lottieData, setLottieData] = React.useState<LottieAnimationData | null>(null);

    React.useEffect(() => {
      if (!isLoading) {
        return;
      }

      const controller = new AbortController();
      let alive = true;

      let lottieFile = '';

      // 1. Black (검정 텍스트) - Solid Primary
      if (
        variant === 'cta-solid-primary' ||
        variant === 'lg-solid-primary' ||
        variant === 'sm-solid-primary'
      ) {
        lottieFile = '/assets/lottie/popow-loading-black.json';
      }
      // 2. White (흰색 텍스트) - Solid Alternative
      else if (
        variant === 'cta-solid-alternative' ||
        variant === 'lg-solid-alternative' ||
        variant === 'sm-solid-alternative'
      ) {
        lottieFile = '/assets/lottie/popow-loading-white.json';
      }
      // 3. Green (초록 텍스트) - Outlined Primary/Alternative
      else if (
        variant === 'lg-outlined-primary' ||
        variant === 'lg-outlined-alternative' ||
        variant === 'sm-outlined-primary' ||
        variant === 'sm-outlined-alternative'
      ) {
        lottieFile = '/assets/lottie/popow-loading-green.json';
      }
      // 4. Gray 88% (회색 텍스트) - IconOnly 및 나머지 모든 버튼
      else {
        lottieFile = '/assets/lottie/popow-loading-292A2D-88.json';
      }

      fetch(lottieFile, { signal: controller.signal })
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to load Lottie: ${res.status} ${res.statusText}`);
          }
          return res.json();
        })
        .then(data => {
          if (alive) {
            setLottieData(data);
          }
        })
        .catch(err => {
          if (controller.signal.aborted) {
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('Lottie load failed:', err);
          }
        });

      return () => {
        alive = false;
        controller.abort();
      };
    }, [isLoading, variant]);

    // Lottie Spinner (20x20px for buttons)
    const defaultSpinner = lottieData ? (
      <Lottie
        animationData={lottieData}
        loop={true}
        autoplay={true}
        style={{ width: 20, height: 20 }}
      />
    ) : (
      // Fallback SVG Spinner
      <svg
        className="h-5 w-5 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const spinner = loadingSpinner || defaultSpinner;

    // Handle fullWidth for CTA buttons
    const widthClass = isCTA && fullWidth === false ? 'w-auto' : '';

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, isLoading, iconOnly }),
          widthClass,
          nonInteractive && 'pointer-events-none',
          className,
        )}
        ref={ref as React.Ref<HTMLButtonElement>}
        // Only pass disabled to actual button elements
        disabled={asChild ? undefined : nonInteractive}
        aria-disabled={nonInteractive ? true : undefined}
        onClick={handleClick}
        {...props}
      >
        {/* IconOnly Mode */}
        {iconOnly &&
          (isLoading ? (
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {spinner}
            </span>
          ) : (
            children
          ))}

        {/* Regular Button Mode */}
        {!iconOnly && (
          <>
            {/* Left Icon */}
            {!isLoading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}

            {/* CTA Loading: Show spinner on right, keep text */}
            {isCTA && isLoading && (
              <>
                <span>{children}</span>
                <span className="flex-shrink-0">{spinner}</span>
              </>
            )}

            {/* Regular Button Loading: Center spinner, hide text */}
            {!isCTA && isLoading && (
              <>
                <span className="opacity-0">{children}</span>
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  {spinner}
                </span>
              </>
            )}

            {/* Normal state (no loading) */}
            {/* Ghost 버튼은 텍스트를 span으로 감싸서 커스텀 밑줄 적용 */}
            {!isLoading && (isGhost ? <span>{children}</span> : children)}

            {/* Right Icon */}
            {!isLoading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
